import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Anchor } from "lucide-react";

const LINKS = [
  { label: "Services", href: "#services", testId: "nav-link-services" },
  { label: "Products", href: "#products", testId: "nav-link-products" },
  { label: "Process", href: "#manifesto", testId: "nav-link-manifesto" },
  { label: "Network", href: "#network", testId: "nav-link-network" },
  { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

export default function Navbar({ onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "py-5 bg-transparent"}`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#top" data-testid="nav-brand-logo" className="flex items-center gap-2 group">
          <img
            src="/logo-gold-transparent.png"
            alt="Suryavansh Exim Pvt. Ltd."
            className="h-16 sm:h-20 w-auto object-contain brightness-125 saturate-150 filter drop-shadow-[0_0_22px_rgba(255,215,0,0.65)] transition-transform duration-300 group-hover:scale-105"
          />
          <div className="leading-snug hidden sm:block">
            <p className="font-display text-base font-extrabold uppercase tracking-widest text-white">Suryavansh</p>
            <p className="text-[0.6rem] font-semibold tracking-[0.2em] uppercase text-[#FFD700]">Exim Pvt. Ltd.</p>
          </div>
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className="relative text-sm font-medium text-slate-300 transition-colors hover:text-white after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#E65100] after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onQuote}
            data-testid="nav-quote-btn"
            className="hidden items-center gap-2 rounded-full bg-[#E65100] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#FF6D00] hover:shadow-[0_0_30px_rgba(230,81,0,0.6)] sm:flex"
          >
            <Anchor className="h-4 w-4" /> Request a Quote
          </button>
          <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="glass overflow-hidden lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-2 text-slate-200">{l.label}</a>
              ))}
              <button onClick={() => { setOpen(false); onQuote(); }} className="mt-3 rounded-full bg-[#E65100] px-5 py-3 font-semibold text-white">
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
